import { ParametersType, PriorityList } from '../constants/telosair-interfaces';

export type StatusInfo = { label: string; badgeClass: string };

export const NO_DATA_STATUS: StatusInfo = { label: 'No data', badgeClass: 'bg-gray-500' };

// Theming for the app's threshold priority buckets (non-AQI parameters),
// using the project's own colour palette.
export const PRIORITY_STATUS: Record<string, StatusInfo> = {
  [PriorityList.low]: { label: 'Good', badgeClass: 'bg-success-500' },
  [PriorityList.medium]: { label: 'Moderate', badgeClass: 'bg-warning-500' },
  [PriorityList.high]: { label: 'Unhealthy', badgeClass: 'bg-error-500' },
  [PriorityList.critical]: { label: 'Unhealthy', badgeClass: 'bg-error-500' },
  [PriorityList.none]: NO_DATA_STATUS
};

/**
 * Whether a parameter key refers to AQI.
 * @param param - The parameter key.
 * @returns True when the parameter is AQI.
 */
export const isAqiParam = (param?: ParametersType): boolean => param === ('aqi' as ParametersType);

/**
 * Maps an AQI value to its US EPA category. AQI is one-directional (higher is
 * worse), so the generic two-sided thresholds must never be used for it.
 * @param value - The AQI value.
 * @returns The matching status label and badge class.
 */
export function getAqiStatus(value: number): StatusInfo {
  if (value <= 50) return { label: 'Good', badgeClass: 'bg-success-500' };
  if (value <= 100) return { label: 'Moderate', badgeClass: 'bg-warning-500' };
  if (value <= 150) return { label: 'Unhealthy for Sensitive', badgeClass: 'bg-warning-600' };
  if (value <= 200) return { label: 'Unhealthy', badgeClass: 'bg-error-500' };
  if (value <= 300) return { label: 'Very Unhealthy', badgeClass: 'bg-error-700' };
  return { label: 'Hazardous', badgeClass: 'bg-error-900' };
}

/**
 * Resolves a status (label + badge class) for a parameter value: EPA AQI
 * categories for AQI, otherwise the app's threshold priority colour.
 * @param param - The parameter key.
 * @param value - The (unit-converted) value.
 * @param priority - The priority returned by getPriorityColor for non-AQI params.
 * @returns The resolved status.
 */
export function resolveStatus(
  param: ParametersType,
  value: number | null,
  priority: PriorityList
): StatusInfo {
  if (value === null) return NO_DATA_STATUS;
  if (isAqiParam(param)) return getAqiStatus(value);
  return PRIORITY_STATUS[priority] || NO_DATA_STATUS;
}
